import React from 'react';
import { Link } from 'react-router-dom';
import { Car, Map, Shield, ChevronRight, Calendar, Users, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const SelfDriveSafariSection = () => {
  const safariPackages = [
    {
      id: 'finest',
      title: "12-Day Uganda's Finest",
      image: 'https://imgur.com/YmQ8sKD.jpg',
      price: 2400,
      duration: '12 days',
      description: 'Experience the ultimate Ugandan adventure with this comprehensive safari package.',
      route: '/self-drive-safaris/ugandas-finest'
    },
    {
      id: 'primate',
      title: '8-Day The Primate Express',
      image: 'https://imgur.com/PK2H77z.jpg',
      price: 2100,
      duration: '8 days',
      description: 'Focus on Uganda\'s incredible primates including gorillas and chimpanzees.',
      route: '/self-drive-safaris/primate-express'
    },
    {
      id: 'circuit',
      title: '24-Day The Grand Circuit',
      image: 'https://imgur.com/2xNtdHj.jpg',
      price: 5100,
      duration: '24 days',
      description: 'A comprehensive exploration of Uganda\'s most iconic destinations.',
      route: '/self-drive-safaris/grand-circuit'
    }
  ];

  return (
    <section className="py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center bg-ta-green/10 rounded-full px-3 py-1 text-sm text-ta-green mb-4"
          >
            <Car className="h-4 w-4 mr-2" />
            Self-Drive Adventures
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            Explore Uganda at Your Own Pace
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            Take control of your adventure with our premium self-drive safari packages. 
            Discover Uganda's breathtaking landscapes and wildlife on your own schedule.
          </motion.p>
        </div>

        {/* Safari Packages - Updated for mobile with 2 cards per row */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-8 mb-12">
          {safariPackages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              className="group bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="relative h-28 sm:h-48">
                <img 
                  src={pkg.image}
                  alt={pkg.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 text-white">
                  <h3 className="text-xs sm:text-xl font-bold line-clamp-1">{pkg.title}</h3>
                  <div className="flex items-center mt-0.5 sm:mt-1">
                    <Calendar className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-ta-green mr-0.5 sm:mr-1" />
                    <span className="text-white/80 text-[8px] sm:text-sm">{pkg.duration}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-2 sm:p-4">
                <p className="text-[10px] sm:text-base text-gray-600 mb-2 sm:mb-4 line-clamp-2">{pkg.description}</p>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[8px] sm:text-xs text-gray-500">from</p>
                    <p className="text-xs sm:text-xl font-bold text-ta-green">${pkg.price}</p>
                  </div>
                  <Link
                    to={pkg.route}
                    className="px-2 py-1 sm:px-4 sm:py-2 bg-ta-green text-white text-[8px] sm:text-base rounded-md sm:rounded-lg hover:bg-ta-green-dark transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-gray-900">Why Choose Self-Drive?</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-ta-green/10 rounded-lg flex items-center justify-center mt-1">
                  <Map className="h-5 w-5 text-ta-green" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold text-gray-900">Freedom & Flexibility</h4>
                  <p className="text-gray-600">Set your own pace, stop whenever you want, and explore off-the-beaten-path destinations.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-ta-green/10 rounded-lg flex items-center justify-center mt-1">
                  <Shield className="h-5 w-5 text-ta-green" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold text-gray-900">Comprehensive Support</h4>
                  <p className="text-gray-600">24/7 assistance, detailed route maps, and emergency support throughout your journey.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-ta-green/10 rounded-lg flex items-center justify-center mt-1">
                  <Users className="h-5 w-5 text-ta-green" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold text-gray-900">Private Experience</h4>
                  <p className="text-gray-600">Enjoy intimate wildlife encounters and cultural experiences with just your group.</p>
                </div>
              </div>
            </div>
            
            <Link 
              to="/self-drive-safaris"
              className="inline-flex items-center px-6 py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors group"
            >
              Explore Self-Drive Options
              <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-ta-green/20 to-transparent rounded-2xl transform -rotate-3 scale-105"></div>
            <div className="relative">
              <img 
                src="https://imgur.com/YABl8r4.jpg" 
                alt="Self-drive safari vehicle" 
                className="rounded-xl shadow-lg"
              />
              
              <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">What's Included:</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-ta-green mr-2" />
                    <span className="text-sm">4x4 Vehicle</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-ta-green mr-2" />
                    <span className="text-sm">Detailed Maps</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-ta-green mr-2" />
                    <span className="text-sm">24/7 Support</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-ta-green mr-2" />
                    <span className="text-sm">Accommodation</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SelfDriveSafariSection;